import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { contentSorting, contentType, routes, searchTileType } from '../../../../constants';
import { isManager, isMinimumBased, mapRecommendedResult, mapUserCpdCategoryProgress } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { businessSelector } from '../../../../state/modules/business';
import { publisherActions } from '../../../../state/modules/publisher';
import { recommendedSearchTileSelector, searchTileActions } from '../../../../state/modules/search-tile';
import { taskActions } from '../../../../state/modules/task';
import { currentUserSelector, userActions } from '../../../../state/modules/user';
import { currentUserCpdDaysRemaining, currentUserIndustry, isUserFinancialAdvisorSelector } from '../../../../state/modules/user/selectors';
import { noCapMap, pluralise } from '../../../../utils';
import { ContentFilterGroup, FormField, Icon, SearchToggler } from '../../../components';
import { ProgressChart } from '../../../partials';
import Loading from '../../loading';
import ProgressCard from '../partials/progress-card';
import SliderSection from '../partials/slider-section';
import ContentList, { searchTileFilter } from './partials/content-list';

const SLIDES_PER_SECTION = 7;

const Home = (props) => (
  <div className='base-layout'>
    <section id='header--training' className='layout--header'>
      <Grid>
        <Row className='no-gutter'>
          <Col xs={12} md={7}>
            <Breadcrumb>
              <LinkContainer to={props.link}>
                <BreadcrumbItem active>
                  {props.heading}
                </BreadcrumbItem>
              </LinkContainer>
            </Breadcrumb>
            <h1><Icon icon='training-large' />{props.heading}<Icon className='search-toggle-button' icon='search-large' onClick={(e) => props.onSearchIconClick(e)} /></h1>
            <Row className='no-gutter top-gap bottom-gap'>
              {
                props.showExternalCpd ?
                  <Col xs={6} md={4} mdOffset={0} className='no-padding'>
                    <LinkContainer to={routes.app.externalCpd.details} className='large full'>
                      <Button>
                        Add External CPD <Icon icon='add-large' />
                      </Button>
                    </LinkContainer>
                  </Col> : null
              }
            </Row>
          </Col>
          <ProgressCard {...{ ...props.overallCpdProgress, totalCpdCategorys: props.allCpdCategorys.length, showPublish: props.showPublish, showExternalCpd: props.showExternalCpd }} />
        </Row>
        <Row className='no-gutter top-gap'>
          <Col xs={12} md={9} className='top-gap flexbox nowrap'>
            <span className='right-gap'>
              <SearchToggler onChange={props.onSearchChange} />
            </span>
            <ContentFilterGroup onApply={props.setFilters} />
          </Col>
          <Col xs={12} md={3}>
            <form className={classNames('searchToggle searchDesktop', { block: props.displaySearchBlock })}>
              <FormField id='sort-by' label='Sort by' type='select-item' required>
                <select
                  id='sort-by'
                  value={props.sort}
                  required
                  onChange={(e) => props.sortBy(e)}>
                  {
                    fp.map((sortingKey) =>
                      <option key={sortingKey} value={sortingKey}>{fp.get(sortingKey, contentSorting).displayName}</option>
                    )(fp.keys(contentSorting))
                  }
                </select>
              </FormField>
            </form>
          </Col>
        </Row>
      </Grid>
    </section>
    <ComposedRecommendedContentSection {...props} />
  </div>
);

const RecommendedContentSection = ({
  cpdDaysRemaining,
  recommendedResult,
  isFetchingSearchTile,
  cpdCategorySearchTiles,
  allCpdCategorys,
  mappedCpdCategorys,
  isMinimumBased,
  isTrainingPlanManager,
  industryCpdCategorys
}) => {
  const userSpecificSlider = fp.flow(
    fp.sortBy([
      ({ shortFall }) => -1 * shortFall,
      'name'
    ]),
    noCapMap(({
      cpdCategoryId,
      progress,
      shortFall,
      title,
      name,
      hide,
      isChildCpdCategory,
      goal,
      icon
    }, index) => {
      const showMore = fp.get('hits')(fp.find((o => fp.contains(cpdCategoryId)(o.cpdCategorys)))(cpdCategorySearchTiles)) > SLIDES_PER_SECTION
      return hide ? null : <SliderSection
        key={index}
        id={title}
        autoPlay={false}
        hideButtonLink={true}
        className='recommended'
        noDataText='There is no recommended content for this CPD category yet.'
        heading={
          <span className='chart-lockup'>
            <div className='chart'>
              {
                isMinimumBased ?
                  icon ? <img className="category-icon" src={he.decode(icon)} alt={name} /> : null :
                  <ProgressChart progressPercentage={progress} width={100} height={100} />
              }
            </div>
            {title}
            <span>
              {!isChildCpdCategory || (goal > 0) ?
                `${shortFall} ${pluralise('hour', shortFall)} to go ${cpdDaysRemaining ? `with ${cpdDaysRemaining} ${pluralise('day', cpdDaysRemaining)} to go` : ''}`
                : ''} &nbsp;
            </span>
          </span>
        }
        link={routes.app.training.cpdCategory(cpdCategoryId)}
        slides={recommendedResult(fp.find((o => fp.contains(cpdCategoryId)(o.cpdCategorys)))(cpdCategorySearchTiles))}
        loading={isFetchingSearchTile}
        showMore={showMore ? `See all ${he.decode(name)} content` : null} />
    }
    )
  )(mappedCpdCategorys)

  const industrySlider = noCapMap(
    ({
      id,
      name
    }, index) => {
      const showMore = fp.get('hits')(fp.find((o => fp.contains(id)(o.cpdCategorys)))(cpdCategorySearchTiles)) > SLIDES_PER_SECTION
      return <SliderSection
        key={index}
        id={name}
        autoPlay={false}
        hideButtonLink={true}
        className='recommended'
        noDataText='There is no recommended content for this CPD category yet.'
        heading={he.decode(name)}
        link={routes.app.training.cpdCategory(id)}
        slides={recommendedResult(fp.find((o => fp.contains(id)(o.cpdCategorys)))(cpdCategorySearchTiles))}
        loading={isFetchingSearchTile}
        showMore={showMore ? `See all ${he.decode(name)} content` : null} />
    }
  )(industryCpdCategorys)

  const genericSlider = noCapMap(
    ({
      cpdCategoryId,
      title
    }, index) => {
      const showMore = fp.get('hits')(fp.find((o => fp.contains(cpdCategoryId)(o.cpdCategorys)))(cpdCategorySearchTiles)) > SLIDES_PER_SECTION
      return <SliderSection
        key={index}
        id={title}
        autoPlay={false}
        hideButtonLink={true}
        className='recommended'
        noDataText='There is no recommended content for this CPD category yet.'
        heading={title}
        link={routes.app.training.cpdCategory(cpdCategoryId)}
        slides={recommendedResult(fp.find((o => fp.contains(cpdCategoryId)(o.cpdCategorys)))(cpdCategorySearchTiles))}
        loading={isFetchingSearchTile}
        showMore={showMore ? `See all ${he.decode(title)} content` : null} />
    }
  )(allCpdCategorys)

  return (
    <section id='section--training' className='layout--section'>
      {
        (mappedCpdCategorys.length > 0) ?
          isTrainingPlanManager ?
            industrySlider :
            userSpecificSlider :
          genericSlider
      }
    </section>
  )
}

const SearchResult = ({ filter }) => <ContentList filter={filter} />

const defaultCpdCategoryFilter = ({ industryCpdCategorys }) => ({ cpdCategoryId, ...aoa }) => {
  const nonParentCpdCategory = fp.filter(
    ({ id }) => !fp.find(
      ({ parentCpdCategoryId }) => parentCpdCategoryId === id
    )(industryCpdCategorys)
  )(industryCpdCategorys)
  const childCpdCategory = fp.filter(({ parentCpdCategoryId }) => parentCpdCategoryId)(industryCpdCategorys)
  return {
    ...aoa,
    cpdCategoryId,
    hide: !fp.find(
      ({ id }) => id === cpdCategoryId
    )(nonParentCpdCategory),
    isChildCpdCategory: fp.find(({ id }) => id === cpdCategoryId)(childCpdCategory)
  }
}

const ComposedRecommendedContentSection = compose(
  branch(
    ({ isFetchingCpdCategorys, isFetchingUserCpdCategorys, userCpdCategoryProgress }) => isFetchingCpdCategorys || isFetchingUserCpdCategorys || !userCpdCategoryProgress,
    renderComponent(Loading)
  ),
  branch(
    ({ searchQuery, filters }) => !fp.isEmpty(searchQuery) || !fp.isEmpty(filters),
    renderComponent(SearchResult)
  )
)(RecommendedContentSection);

const mapStateToProps = (state) => ({
  business: businessSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  userCpdCategoryProgress: state.user.cpdCategoryProgress,
  currentUserCpdCategorys: state.user.cpdCategories,
  industryCpdCategorys: state.industry.cpdCategorys,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys,
  isFetchingUserCpdCategorys: state.user.currentUserCpdCategorysIsFetching,
  isFetchingSearchTile: state.searchTile.isFetching,
  recommendedResult: mapRecommendedResult(state.industry.cpdCategorys, state.publisher.publishers, businessSelector(state), state.user.currentUserBookmarks),
  userState: state.user,
  currentUser: currentUserSelector(state),
  isMinimumBased: isMinimumBased(currentUserIndustry(state)),
  cpdCategorySearchTiles: recommendedSearchTileSelector(state),
  isUserFinancialAdvisor: isUserFinancialAdvisorSelector(state),
  userProfile: state.user.profile
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getContent: bindActionCreators(searchTileActions.searchTile.get, dispatch),
  getRecommended: bindActionCreators(searchTileActions.searchTile.getRecommended, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getTaskCount: bindActionCreators(taskActions.task.getCount, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ userProfile, industriesWithCpdCategorys }) => {
      const userGoalIds = fp.flow(
        fp.get('trainingPlans'),
        fp.flatMap('goals'),
        fp.map('goalId')
      )(userProfile)
      const allGoals = fp.uniq(fp.flatMap('goals')(industriesWithCpdCategorys))
      const userGoals = fp.filter(
        ({ id }) => fp.includes(id)(userGoalIds)
      )(allGoals)
      return {
        userGoals
      }
    }
  ),
  withStateHandlers(
    {
      initiatedContentLoading: false,
      displaySearchBlock: false,
      searchQuery: '',
      searchDebounce: undefined,
      sort: fp.head(fp.keys(contentSorting))
    },
    {
      onSortByChange: () => (e) => ({
        sort: e.target.value
      }),
      onSearchChange: () => (searchQuery) => ({
        searchQuery
      }),
      setSearchDebounce: () => (searchDebounce) => ({
        searchDebounce
      }),
      onSearchIconClick: ({ displaySearchBlock }) => () => ({
        displaySearchBlock: !displaySearchBlock
      }),
      contentLoadingInitiated: () => () => ({
        initiatedContentLoading: true
      })
    }
  ),
  branch(
    ({ userCpdCategoryProgress }) => !userCpdCategoryProgress,
    renderComponent(Loading)
  ),
  withProps(({ cpdCategorys, currentUserCpdCategorys, industryCpdCategorys, userCpdCategoryProgress, userState, currentUser, overallCpdProgress, parentCpdCategoryId }) => ({
    isTrainingPlanManager: isManager(currentUser),
    mappedCpdCategorys: fp.flow(
      fp.filter(
        ({ userId, cpdCategoryId }) => userId && !fp.flow(fp.find(({ id }) => id === cpdCategoryId), fp.get('legacy'))(cpdCategorys)
      ),
      fp.map(
        (aoa) => ({
          ...aoa,
          icon: fp.find(({ id }) => id === aoa.cpdCategoryId)(cpdCategorys).iconUrl
        })
      ),
      (recommendedCpdCategorys) => {
        const childrenCpdCategoryIds = fp.flow(
          fp.filter(
            ({ parentCpdCategoryId }) => !fp.isNil(parentCpdCategoryId)
          ),
          fp.map('id')
        )(industryCpdCategorys)
        const childrenCpdCategorysSelected = fp.filter(
          ({ cpdCategoryId }) => fp.contains(cpdCategoryId)(childrenCpdCategoryIds)
        )(currentUserCpdCategorys)

        return childrenCpdCategorysSelected.length > 0 ?
          fp.filter(
            ({ cpdCategoryId }) => fp.includes(cpdCategoryId)(fp.map('cpdCategoryId')(currentUserCpdCategorys))
          )(recommendedCpdCategorys) :
          recommendedCpdCategorys
      }
    )(mapUserCpdCategoryProgress(industryCpdCategorys)(userCpdCategoryProgress)),
    userCpdCategoryIds: userState.currentUserCpdCategorys,
    showExternalCpd: parentCpdCategoryId !== undefined ? false : true,
    allCpdCategorys: fp.map((cpdCategory) => ({
      cpdCategoryId: cpdCategory.id,
      title: cpdCategory.name
    }))(cpdCategorys),
    overallCpdProgress: overallCpdProgress ? overallCpdProgress : {
      ...userCpdCategoryProgress.primaryStat,
      ...userCpdCategoryProgress.derivedStat
    }
  })),
  withProps(({ aoaFilter, heading, link, parentCpdCategoryId, industryCpdCategorys, mappedCpdCategorys }) => ({
    aoaFilter: aoaFilter ? aoaFilter : defaultCpdCategoryFilter,
    childrenCpdCategoryId: parentCpdCategoryId ? fp.flow(
      ({ industryCpdCategorys, mappedCpdCategorys }) => {
        const childrenCpdCategoryIds = fp.flow(
          fp.filter(
            a => a.parentCpdCategoryId === parentCpdCategoryId
          ),
          fp.map(
            ({ id }) => id
          )
        )(industryCpdCategorys)
        const userCpdCategoryIds = fp.flow(
          fp.map(
            ({ cpdCategoryId }) => cpdCategoryId
          )
        )(mappedCpdCategorys)
        return fp.intersection(childrenCpdCategoryIds)(userCpdCategoryIds)
      },
      fp.join(',')
    )({ industryCpdCategorys, mappedCpdCategorys }) : undefined,
    heading: heading ? heading : `CPD content`,
    link: link ? link : routes.app.training.home
  })),
  withProps(
    ({ mappedCpdCategorys, industryCpdCategorys, isMinimumBased, aoaFilter }) => ({
      mappedCpdCategorys: isMinimumBased ? fp.map(
        aoaFilter({ industryCpdCategorys })
      )(mappedCpdCategorys) : fp.map(
        (aoa) => ({
          ...aoa,
          hide: false
        })
      )(mappedCpdCategorys)
    })
  ),
  withStateHandlers(
    {
      filters: {}
    },
    {
      setFilters: () => (filters) => ({
        filters
      })
    }
  ),
  withProps(
    ({
      mappedCpdCategorys,
      searchQuery,
      sort,
      filters
    }) => {
      const filter = searchTileFilter(
        [searchTileType.Content].join(','),
        fp.join(',')(filters.cpdCategoryIds),
        sort,
        mappedCpdCategorys,
        searchQuery,
        [filters.publisher],
        filters.business,
        fp.join(',')(filters.associations),
        '',
        filters.min,
        filters.max,
        fp.getOr(null)(filters.contentType)(contentType),
        filters.isBookmarkedOnly,
      )
      return ({
        filter: {
          ...filter,
          goals: fp.join(',')(filters.goalIds)
        }
      })
    }
  ),
  withHandlers({
    refetchData: ({ searchDebounce, getContent, filter, setSearchDebounce }) => () => {
      searchDebounce && searchDebounce.cancel()
      const debounce = fp.debounce(500)(
        () => getContent(0, filter)
      )
      setSearchDebounce(debounce)
      debounce()
    }
  }),
  withHandlers({
    sortBy: ({ onSortByChange }) => (e) => {
      onSortByChange(e);
    },
    getRecommended: ({ getRecommended }) => () => {
      getRecommended({ take: SLIDES_PER_SECTION, searchTileType: searchTileType.Content })
    },
    onCpdCategoryFilterChanged: ({
      updateSelectedCpdCategory,
      cpdCategorys
    }) => (e) => {
      const idToToggle = parseInt(e.target.value, 10);
      const updatedCpdCategorys = fp.flow(
        fp.map(
          ({ id, name, selected, parentCpdCategoryId, legacy }) => ({
            id,
            name,
            parentCpdCategoryId,
            selected: id === idToToggle ? !selected : selected,
            legacy
          })
        )
      )(cpdCategorys);
      updateSelectedCpdCategory(updatedCpdCategorys);
    },
    onAssociationFilterChanged: ({
      updateSelectedAssociation,
      associations
    }) => (e) => {
      const updatedAssociations = fp.map(
        (association) => association.value === e.target.value ? {
          ...association,
          selected: !association.selected
        } : association
      )(associations)
      updateSelectedAssociation(updatedAssociations)
    }
  }),
  withProps(
    ({ selectedPublisher, selectedBusiness, associations, cpdCategorys, selectedMinCpdHours, selectedMaxCpdHours, selectedContentType, selectedSearchTileType }) => ({
      searchByPublisher: selectedPublisher !== undefined || selectedBusiness !== undefined,
      searchByCpdCategory: fp.find(({ selected }) => selected)(cpdCategorys) !== undefined,
      searchByAssociation: fp.find(({ selected }) => selected)(associations) !== undefined,
      searchByCpdHours: selectedMinCpdHours !== undefined || selectedMaxCpdHours !== undefined,
      searchByContentType: selectedContentType || selectedSearchTileType
    })
  ),
  lifecycle({
    componentDidMount() {
      const { cpdCategorys,
        isFetchingCpdCategorys,
        publishers, isFetchingPublishers,
        mappedCpdCategorys,
        getCpdCategorys,
        getPublishers,
        getUserCpdCategorys,
        getTaskCount,
        getRecommended,
        getUserBookmarks,
      } = this.props;

      getTaskCount();
      getUserBookmarks();

      if (fp.isEmpty(cpdCategorys) && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }

      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }
      if (fp.isEmpty(mappedCpdCategorys)) {
        getUserCpdCategorys();
      } else {
        getRecommended(fp.map((x) => x.cpdCategoryId)(mappedCpdCategorys));
      }
    },
    componentWillReceiveProps(nextProps) {
      const {
        isFetchingSearchTile,
        mappedCpdCategorys,
        cpdCategorys,
        isFetchingUserCpdCategorys,
        isFetchingCpdCategorys,
        getRecommended,
        filter,
        refetchData,
        contentLoadingInitiated
      } = this.props;

      const { initiatedContentLoading, filter: nextFilter } = nextProps;

      const fetchedUserCpdCategorys = fp.difference(fp.map(x => x.cpdCategoryId)(mappedCpdCategorys), fp.map(x => x.cpdCategoryId)(nextProps.mappedCpdCategorys));

      // After loading the areas of advice for the user and the site, check to see if the user
      // has any CpdCategory selected. If they have none set up then do a content load on all the content, otherwise
      // always use the current user's selected CpdCategory setting. 
      const cpdCategoryIds = (
        !isFetchingUserCpdCategorys &&
        !isFetchingCpdCategorys &&
        nextProps.mappedCpdCategorys.length === 0 &&
        nextProps.cpdCategorys.length !== 0
      ) ?
        fp.map((x) => x.id)(cpdCategorys) :
        (fetchedUserCpdCategorys);

      // Initiate the fisrt loading of the area of advice's content if:
      //  # it has not been initiated before, and
      //  # area of advices have some content to load.
      if (!initiatedContentLoading && !fp.isEmpty(cpdCategoryIds) && !isFetchingSearchTile) {
        getRecommended();
        contentLoadingInitiated()
      }

      if (JSON.stringify(filter) !== JSON.stringify(nextFilter)) {
        refetchData()
      }
    }
  })
)(Home);



// WEBPACK FOOTER //
// ./src/containers/pages/app/training/home.js